import { useState, useCallback } from "react";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import type { ReceptionByAppointmentResponse } from "~/utils/receptionDB";

const receptionByAppointmentUrl = buildIfaceApiUrl("/v1/receptions/by-appointment");

export interface ReceptionByAppointmentRequest {
  qrCode?: string | null;
  receptionNumber?: number | null;
}

export type ReceptionByAppointmentData = ReceptionByAppointmentResponse["data"];

export interface ApiError {
  errorCode: number;
}

export interface UseReceptionByAppointmentResult {
  isLoading: boolean;
  error: ApiError | null;
  submit: (payload: ReceptionByAppointmentRequest) => Promise<ReceptionByAppointmentData>;
}

/**
 * 予約に紐づく受付情報を取得するための API 呼び出しを行うカスタムフック。
 *
 * `submit` では `ReceptionByAppointmentRequest` を送信し、`qrCode` または
 * `receptionNumber` を payload として `/v1/receptions/by-appointment` に POST する。
 *
 * @returns 送信状態を表す `isLoading`、直近の API エラーを保持する `error`、
 * および受付情報 `ReceptionByAppointmentData` を返す `submit` を含む。
 * `submit` は API エラー時に `errorCode` を持つ `ApiError` を throw する。
 * @throws {ApiError} API リクエストに失敗した場合に throw される。
 */
export const useReceptionByAppointment = (): UseReceptionByAppointmentResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submit = useCallback(
    async (payload: ReceptionByAppointmentRequest): Promise<ReceptionByAppointmentData> => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await apiClient.post<ReceptionByAppointmentResponse>(
          receptionByAppointmentUrl,
          payload,
          {
            headers: createIfaceHeaders(true),
          }
        );

        return res.data.data;
      } catch (err: unknown) {
        // 安全に errorCode を取り出す
        const maybeErr = err as { response?: { data?: { errorCode?: unknown } } } | undefined;
        const rawCode = maybeErr?.response?.data?.errorCode;
        const errorCode = typeof rawCode === "number" ? rawCode : 500;

        const apiError: ApiError = { errorCode };

        setError(apiError);
        throw apiError;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    submit,
  };
};
